import { useState, useEffect, useRef } from "react";
import { Search, Bell, X, AlertTriangle, MapPin, Clock, CheckCircle, Wifi, WifiOff } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";

function timeAgo(isoStr) {
  if (!isoStr) return null;
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0)  return `${d}d ago`;
  if (h > 0)  return `${h}h ago`;
  if (m > 0)  return `${m}m ago`;
  return "just now";
}

export default function Navbar({ vehicles, onSearch, searchQuery, extraRight }) {
  const [, setUserOpen]    = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen]   = useState(false);
  const isMobile  = useIsMobile();
  const bellRef   = useRef(null);
  const panelRef  = useRef(null);

  const alerts  = vehicles.filter(v => v.noRecentData).length;
  const active  = vehicles.filter(v => v.status === "Active").length;
  const offline = vehicles.filter(v => v.status === "Offline").length;

  // Build notification items
  const notifItems = [];
  vehicles.forEach(v => {
    if (v.noRecentData) {
      notifItems.push({ type: "no_data", vehicle: v });
    } else if (v.isTracking && v.lastUpdated) {
      const diffH = (Date.now() - new Date(v.lastUpdated).getTime()) / 3600000;
      if (diffH > 24) notifItems.push({ type: "stale", vehicle: v, diffH });
    }
  });
  // Offline vehicles (not tracking, no data) last
  vehicles.forEach(v => {
    if (!v.isTracking && !v.noRecentData) {
      notifItems.push({ type: "offline", vehicle: v });
    }
  });

  // Close panel when clicking outside
  useEffect(() => {
    if (!notifOpen) return;
    function handle(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current  && !bellRef.current.contains(e.target)
      ) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [notifOpen]);

  return (
    <header style={{
      background: "#ffffff",
      borderBottom: "1px solid #EBEBEB",
      display: "flex",
      alignItems: "center",
      paddingTop: isMobile ? "var(--sat, 0px)" : 0,
      paddingBottom: 0,
      paddingLeft: isMobile ? 12 : 20,
      paddingRight: isMobile ? 12 : 20,
      minHeight: isMobile ? "calc(62px + var(--sat, 0px))" : 62,
      gap: isMobile ? 10 : 16,
      flexShrink: 0,
      zIndex: 100,
      position: "relative",
    }}>
      {/* Logo */}
      <div style={{ display:"flex", alignItems:"center", gap:9, flexShrink:0 }}>
        <div style={{
          width:30, height:30, borderRadius:8,
          background:"#111827",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
            <circle cx="8" cy="8" r="2" fill="#fff"/>
          </svg>
        </div>
        <span style={{ fontSize:15, fontWeight:800, color:"#111827", letterSpacing:-0.4 }}>LORRYKHOJO</span>
      </div>

      {/* Desktop: divider + fleet summary + search */}
      {!isMobile && (
        <>
          <div style={{ width:1, height:22, background:"#EBEBEB", flexShrink:0 }} />

          {vehicles.length > 0 && (
            <div style={{ display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
              <span style={{ fontSize:13, color:"#9CA3AF" }}>
                <span style={{ fontWeight:700, color:"#111827" }}>{vehicles.length}</span> vehicles
              </span>
              {active > 0 && (
                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:"#10B981" }} />
                  <span style={{ fontSize:13, fontWeight:600, color:"#059669" }}>{active} live</span>
                </div>
              )}
              {offline > 0 && (
                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:"#D1D5DB" }} />
                  <span style={{ fontSize:13, color:"#9CA3AF" }}>{offline} offline</span>
                </div>
              )}
            </div>
          )}

          <div style={{ flex:1, maxWidth:380, position:"relative", margin:"0 4px" }}>
            <Search
              size={13} color="#B0B7C3"
              style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}
            />
            <input
              value={searchQuery || ""}
              onChange={e => onSearch?.(e.target.value)}
              placeholder="Search vehicles, plates, locations…"
              style={{
                width:"100%", height:38, paddingLeft:34, paddingRight: searchQuery ? 34 : 12,
                background:"#F5F5F5", border:"1.5px solid transparent", borderRadius:9,
                fontSize:13, color:"#111827", outline:"none",
                transition:"border-color 0.15s, background 0.15s",
                boxSizing:"border-box",
              }}
              onFocus={e  => { e.target.style.borderColor="#111827"; e.target.style.background="#fff"; }}
              onBlur={e   => { e.target.style.borderColor="transparent"; e.target.style.background="#F5F5F5"; }}
            />
            {searchQuery && (
              <button
                onClick={() => onSearch?.("")}
                style={{
                  position:"absolute", right:9, top:"50%", transform:"translateY(-50%)",
                  background:"none", border:"none", cursor:"pointer", color:"#B0B7C3",
                  padding:2, display:"flex",
                }}
              ><X size={12} /></button>
            )}
          </div>
        </>
      )}

      {/* Mobile: expandable search */}
      {isMobile && searchOpen && (
        <div style={{ flex:1, position:"relative" }}>
          <Search
            size={13} color="#B0B7C3"
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}
          />
          <input
            autoFocus
            value={searchQuery || ""}
            onChange={e => onSearch?.(e.target.value)}
            placeholder="Search plates, location…"
            style={{
              width:"100%", height:34, paddingLeft:32, paddingRight:32,
              background:"#F5F5F5", border:"1.5px solid #111827", borderRadius:9,
              fontSize:13, color:"#111827", outline:"none",
              boxSizing:"border-box",
            }}
          />
          <button
            onClick={() => { setSearchOpen(false); onSearch?.(""); }}
            style={{
              position:"absolute", right:8, top:"50%", transform:"translateY(-50%)",
              background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", display:"flex",
            }}
          ><X size={13} /></button>
        </div>
      )}

      {/* Right slot */}
      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
        {/* Mobile: search toggle */}
        {isMobile && !searchOpen && (
          <button
            onClick={() => setSearchOpen(true)}
            style={{
              border:"1.5px solid #EBEBEB", borderRadius:8,
              width:38, height:38, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              background:"none", transition:"background 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.background="#F5F5F5"}
            onMouseOut={e  => e.currentTarget.style.background="none"}
          >
            <Search size={14} color="#6B7280" />
          </button>
        )}

        {/* Alert bell */}
        {!searchOpen && (
          <button
            ref={bellRef}
            onClick={() => setNotifOpen(v => !v)}
            style={{
              position:"relative", border:"1.5px solid #EBEBEB",
              borderRadius:8, width:38, height:38, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.15s",
              background: notifOpen ? "#FEF3C7" : (alerts > 0 ? "#FFF9EC" : "none"),
              borderColor: alerts > 0 ? "#FDE68A" : "#EBEBEB",
            }}
            onMouseOver={e => { e.currentTarget.style.background = alerts > 0 ? "#FEF3C7" : "#F5F5F5"; }}
            onMouseOut={e  => { e.currentTarget.style.background = notifOpen ? "#FEF3C7" : (alerts > 0 ? "#FFF9EC" : "none"); }}
          >
            <Bell size={14} color={alerts > 0 ? "#D97706" : "#9CA3AF"} />
            {alerts > 0 && (
              <div style={{
                position:"absolute", top:-5, right:-5,
                minWidth:16, height:16, borderRadius:99,
                background:"#F59E0B", border:"2px solid #fff",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:8, fontWeight:700, color:"#fff", padding:"0 3px",
              }}>{alerts}</div>
            )}
          </button>
        )}

        {/* Notification panel */}
        {notifOpen && (
          <div
            ref={panelRef}
            style={{
              position:"fixed",
              top: 70,
              right: isMobile ? 12 : 20,
              width: isMobile ? "calc(100vw - 24px)" : 360,
              maxHeight: 520,
              background:"#ffffff",
              border:"1px solid #E5E7EB",
              borderRadius:16,
              boxShadow:"0 16px 48px rgba(0,0,0,0.14), 0 4px 12px rgba(0,0,0,0.06)",
              zIndex: 9999,
              overflow:"hidden",
              display:"flex",
              flexDirection:"column",
            }}
          >
            {/* Panel header */}
            <div style={{
              padding:"14px 16px 12px",
              borderBottom:"1px solid #F3F4F6",
              display:"flex", alignItems:"center", justifyContent:"space-between",
              flexShrink:0,
            }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>Notifications</div>
                <div style={{ fontSize:12, color:"#9CA3AF", marginTop:1 }}>
                  {notifItems.length > 0
                    ? `${alerts} alert${alerts !== 1 ? "s" : ""}${notifItems.length > alerts ? `, ${notifItems.length - alerts} info` : ""}`
                    : "All clear — no alerts"}
                </div>
              </div>
              <button
                onClick={() => setNotifOpen(false)}
                style={{
                  width:28, height:28, borderRadius:8, border:"none",
                  background:"#F5F5F5", cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}
                onMouseOver={e => e.currentTarget.style.background="#E5E7EB"}
                onMouseOut={e  => e.currentTarget.style.background="#F5F5F5"}
              >
                <X size={13} color="#6B7280" />
              </button>
            </div>

            {/* Items */}
            <div style={{ overflowY:"auto", flex:1 }}>
              {notifItems.length === 0 && (
                <div style={{ padding:"36px 20px", textAlign:"center" }}>
                  <div style={{
                    width:44, height:44, borderRadius:12, background:"#F0FDF4",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    margin:"0 auto 12px",
                  }}>
                    <CheckCircle size={20} color="#16A34A" />
                  </div>
                  <div style={{ fontSize:14, fontWeight:600, color:"#111827", marginBottom:4 }}>All vehicles OK</div>
                  <div style={{ fontSize:12, color:"#9CA3AF" }}>No alerts at this time</div>
                </div>
              )}

              {notifItems.map((item, i) => {
                const v = item.vehicle;
                const isLast = i === notifItems.length - 1;

                if (item.type === "no_data") {
                  return (
                    <div key={v.id} style={{
                      padding:"12px 16px",
                      borderBottom: isLast ? "none" : "1px solid #F9FAFB",
                      display:"flex", gap:12, alignItems:"flex-start",
                    }}>
                      <div style={{
                        width:38, height:38, borderRadius:10, flexShrink:0,
                        background:"#FFF7ED",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <AlertTriangle size={16} color="#D97706" />
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{v.plateNumber}</span>
                          <span style={{
                            fontSize:10, fontWeight:600, color:"#D97706",
                            background:"#FEF3C7", borderRadius:4, padding:"1px 6px",
                          }}>NO DATA</span>
                        </div>
                        <div style={{ fontSize:12, color:"#6B7280", marginBottom:3 }}>
                          No FASTag crossings recorded in the last 30 days
                        </div>
                        {v.location && (
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <MapPin size={10} color="#9CA3AF" />
                            <span style={{ fontSize:11, color:"#9CA3AF", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {v.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                if (item.type === "stale") {
                  const ago = timeAgo(v.lastUpdated);
                  return (
                    <div key={v.id} style={{
                      padding:"12px 16px",
                      borderBottom: isLast ? "none" : "1px solid #F9FAFB",
                      display:"flex", gap:12, alignItems:"flex-start",
                    }}>
                      <div style={{
                        width:38, height:38, borderRadius:10, flexShrink:0,
                        background:"#EFF6FF",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <Clock size={16} color="#2563EB" />
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{v.plateNumber}</span>
                          <span style={{
                            fontSize:10, fontWeight:600, color:"#2563EB",
                            background:"#DBEAFE", borderRadius:4, padding:"1px 6px",
                          }}>STALE</span>
                        </div>
                        <div style={{ fontSize:12, color:"#6B7280", marginBottom:3 }}>
                          Last toll crossing recorded {ago}
                        </div>
                        {v.location && (
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <MapPin size={10} color="#9CA3AF" />
                            <span style={{ fontSize:11, color:"#9CA3AF", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {v.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                if (item.type === "offline") {
                  return (
                    <div key={v.id} style={{
                      padding:"12px 16px",
                      borderBottom: isLast ? "none" : "1px solid #F9FAFB",
                      display:"flex", gap:12, alignItems:"flex-start",
                    }}>
                      <div style={{
                        width:38, height:38, borderRadius:10, flexShrink:0,
                        background:"#F9FAFB",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <WifiOff size={16} color="#9CA3AF" />
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:"#374151" }}>{v.plateNumber}</span>
                          <span style={{
                            fontSize:10, fontWeight:600, color:"#6B7280",
                            background:"#F3F4F6", borderRadius:4, padding:"1px 6px",
                          }}>OFFLINE</span>
                        </div>
                        <div style={{ fontSize:12, color:"#9CA3AF", marginBottom:3 }}>
                          {v.nickname && v.nickname !== v.plateNumber ? v.nickname : "Tracking not started"}
                        </div>
                        {v.lastUpdated && (
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <Clock size={10} color="#9CA3AF" />
                            <span style={{ fontSize:11, color:"#9CA3AF" }}>
                              Last seen {timeAgo(v.lastUpdated)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {/* Footer */}
            {vehicles.length > 0 && (
              <div style={{
                padding:"10px 16px",
                borderTop:"1px solid #F3F4F6",
                display:"flex", alignItems:"center", justifyContent:"space-between",
                background:"#FAFAFA", flexShrink:0,
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <Wifi size={11} color="#10B981" />
                    <span style={{ fontSize:11, color:"#6B7280" }}>{active} active</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <WifiOff size={11} color="#D1D5DB" />
                    <span style={{ fontSize:11, color:"#6B7280" }}>{offline} offline</span>
                  </div>
                </div>
                <span style={{ fontSize:11, color:"#9CA3AF" }}>{vehicles.length} total vehicles</span>
              </div>
            )}
          </div>
        )}

        {/* Extra right (injected by pages) */}
        {!searchOpen && (extraRight || (
          <button
            onClick={() => setUserOpen(v => !v)}
            style={{
              display:"flex", alignItems:"center", gap:7,
              background:"none", border:"1.5px solid #EBEBEB", borderRadius:8,
              padding:"0 11px", height:38, cursor:"pointer",
              transition:"background 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.background="#F5F5F5"}
            onMouseOut={e  => e.currentTarget.style.background="none"}
          >
            <div style={{
              width:22, height:22, borderRadius:"50%",
              background:"#111827",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:9, fontWeight:700, color:"#fff",
            }}>A</div>
            {!isMobile && <span style={{ fontSize:12, fontWeight:500, color:"#374151" }}>Account</span>}
          </button>
        ))}
      </div>
    </header>
  );
}
